<template>
  <div ref="containerRef" class="tracking-status-container mt-4">
    <div class="tracking-status-left">
      <img
        class="card-image"
        :src="isFailedStatus ? errorImageSrc : cardImageSrc"
        alt="Card"
      />

      <div
        ref="lineRef"
        class="dotted-line"
        :style="{ height: dottedLineHeight + 'px' }"
      ></div>

      <div
        ref="arrowRef"
        class="triangle-up"
        :style="arrowStyle"
        aria-hidden="true"
      ></div>
    </div>

    <div class="tracking-status-right-scroll">
      <template v-if="sortedStatuses.length">
        <div v-if="!showAll" class="status-row">
          <div
            :class="
              isFailedStatus ? 'status-title-failed' : 'status-title-current'
            "
          >
            {{ sortedStatuses[0].title }}
          </div>
          <div class="status-desc-current">
            {{ sortedStatuses[0].description }}
          </div>
        </div>
        <div v-else>
          <div
            v-for="(status, idx) in sortedStatuses"
            :key="status.id"
            class="status-row"
          >
            <div
              :class="
                idx === 0 && isFailedStatus
                  ? 'status-title-failed'
                  : idx === 0
                  ? 'status-title-current'
                  : 'status-title-other'
              "
            >
              {{ status.title }}
            </div>
            <div
              :class="idx === 0 ? 'status-desc-current' : 'status-desc-other'"
            >
              {{ status.description }}
            </div>
          </div>
        </div>
        <div ref="toggleRef" class="toggle-updates" @click="toggleShowAll">
          {{
            showAll
              ? t("PAYJOY_SHIPMENT_DETAILS.HIDE_UPDATES")
              : t("PAYJOY_SHIPMENT_DETAILS.SHOW_UPDATES")
          }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  onMounted,
  computed,
  nextTick,
  watch,
  onBeforeUnmount,
  defineProps,
} from "vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { useI18n } from "@/apps/credit-line/composables/useI18n";

const { t } = useI18n();

const cardImageSrc = useAsset("img/credit-line/credit-card/physical-card.svg");
const errorImageSrc = useAsset(
  "img/credit-line/credit-card/shipment-error.svg"
);
const showAll = ref(false);

const props = defineProps({
  shipmentLogs: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const arrowStyle = computed(() => {
  if (arrowTop.value !== null) {
    return {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
      top: arrowTop.value + "px",
      zIndex: 2,
    };
  }
  return {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    top: "0px",
    zIndex: 2,
  };
});

const sortedStatuses = computed(() => props.shipmentLogs);

const isFailedStatus = computed(() => {
  if (!sortedStatuses.value.length) return false;
  const latest = sortedStatuses.value[0];
  return (
    latest.title ===
    t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_ERROR_STATUS_TITLE")
  );
});

const dottedLineHeight = ref(100);

const lineRef = ref<HTMLElement | null>(null);

function updateDottedLineHeight() {
  if (
    !lineRef.value ||
    !arrowRef.value ||
    !containerRef.value ||
    !toggleRef.value
  )
    return;

  const containerRect = containerRef.value.getBoundingClientRect();
  const imgRect = containerRef.value
    .querySelector(".card-image")
    ?.getBoundingClientRect();
  const toggleRect = toggleRef.value.getBoundingClientRect();

  if (!imgRect) return;

  // The line should go from below the image to the top of the triangle (which is aligned with the center of the toggle)
  const triangleHeight = 12;
  const start = imgRect.bottom - containerRect.top;
  const end =
    toggleRect.top -
    containerRect.top +
    (toggleRect.height - triangleHeight) / 2;

  dottedLineHeight.value = Math.max(0, end - start);
}

function forceDottedLineUpdate() {
  nextTick().then(() => {
    updateArrowPosition();
    updateDottedLineHeight();
  });
}

const containerRef = ref<HTMLElement | null>(null);
const toggleRef = ref<HTMLElement | null>(null);
const arrowRef = ref<HTMLElement | null>(null);
const arrowTop = ref<number | null>(null);

async function updateArrowPosition() {
  await nextTick();
  if (!toggleRef.value || !containerRef.value || !arrowRef.value) {
    arrowTop.value = null;
    return;
  }

  const containerRect = containerRef.value.getBoundingClientRect();
  const toggleRect = toggleRef.value.getBoundingClientRect();
  // The triangle should align with the center of the toggle
  const triangleHeight = 12;
  const topRelative =
    toggleRect.top -
    containerRect.top +
    (toggleRect.height - triangleHeight) / 2;
  arrowTop.value = topRelative;
}

watch([showAll, sortedStatuses], forceDottedLineUpdate);

onMounted(async () => {
  await nextTick();
  updateArrowPosition();
  updateDottedLineHeight();
  window.addEventListener("resize", forceDottedLineUpdate);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", forceDottedLineUpdate);
});

function toggleShowAll() {
  showAll.value = !showAll.value;
  nextTick().then(() => {
    updateArrowPosition();
    updateDottedLineHeight();
  });
}
</script>

<style scoped>
:root {
  --floating-height: 70px;
  --container-padding: 16px;
}

.tracking-status-container {
  box-sizing: border-box;
  width: 100%;
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: var(--container-padding);
  padding-bottom: calc(var(--container-padding) + var(--floating-height));
  min-height: 160px;
  position: relative;
}

.tracking-status-left {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 72px;
  min-width: 60px;
}

.card-image {
  width: 48px;
  height: auto;
  object-fit: contain;
  margin-bottom: 8px;
}

.dotted-line {
  width: 2px;
  border-right: 2px dashed #d1d1d1;
  margin: 0 auto;
  transition: height 0.25s ease;
}

.triangle-up {
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 12px solid #8b8b8b;
  pointer-events: none;
  position: absolute;
}

.tracking-status-right-scroll {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.status-row {
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
  min-width: 0;
}

.status-title-current,
.status-title-other {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}

.status-desc-current,
.status-desc-other {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 20px;
}

.status-title-current {
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #007c3b;
}
.status-desc-current {
  font-size: 14px;
  color: #8b8b8b;
}
.status-title-other {
  font-size: 14px;
  line-height: 21px;
  font-weight: 500;
  color: #d1d1d1;
}
.status-desc-other {
  font-size: 14px;
  color: #8b8b8b;
}
.status-title-failed {
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #d32f2f;
}

.toggle-updates {
  color: #007c3b;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  user-select: none;
  display: inline-block;
  margin-bottom: 25px;
}

.tracking-status-container,
.tracking-status-right-scroll {
  overflow-x: hidden;
}

.tracking-status-right-scroll * {
  word-break: break-word;
  overflow-wrap: anywhere;
}
</style>
